import type { ToSubOptions } from '@tanstack/react-router';
import type { Dayjs } from 'dayjs';

import type {
  ApiEstimationAdditionalFieldType,
  ApiEstimationItemPurpose,
  ApiEstimationItemStatus,
  ApiEstimationStage,
  ApiIsCalculatedMode,
  ApiSortEstimationsPerProjectBy,
  ApiSortUsersBy,
  EstimationItemAssumptionResponse,
  EstimationItemParameterResponse,
  NotificationType,
} from '@/api/schema/data-contracts';

export type TranslationObject = { [key: string]: string | TranslationObject };

export interface Languages {
  key: string;
  label: string;
  enabled: boolean;
}

export const SystemLanguages = [
  { code: 'en-GB', name: 'English' },
  { code: 'de-DE', name: 'German' },
];

export interface Notification {
  id: string;
  message: string;
  timeCreated: Dayjs;
  isRead: boolean;
  estimationId: string;
  conversationId?: string | null;
  type: NotificationType;
}

export interface CurrentEstimateRecord {
  estimationId: string;
  projectName: string;
  planningResponsible: string;
  method: string;
  projectType: string;
  status: ApiEstimationStage;
  estimate: number;
  estimationUnit: string;
  createdAt: Dayjs;
  updatedAt: Dayjs | null;
  createdBy: string;
  userCanEdit: boolean;
  userCanDelete: boolean;
}

export interface AdditionalField {
  estimationModelAdditionalFieldDefinitionId: string;
  value?: string | null;
  isRequired?: boolean;
}

export interface PhasesRequest {
  title: string;
  body: string;
}

export interface PhaseResponse {
  id: string;
  methodologyId?: string | null;
  parameters?: EstimationItemParameterResponse[];
  parameterValues?: [];
  estimationModelItemId: string;
  assumptions: EstimationItemAssumptionResponse[];
  index?: number;
  status: ApiEstimationItemStatus;
  purpose: ApiEstimationItemPurpose;
  name: string;
  description: string;
  omitReason?: string;
  methodology: string;
  isRequired?: boolean;
  isCalculated: ApiIsCalculatedMode;
  isQuestionAnswered: boolean;
  selected?: boolean;
  calculationFormula?: string;
  calculatedResult?: number | null;
}

export interface PhasesProgress {
  phase: number;
  step: number;
}

export interface Phases {
  phasesProgress: PhasesProgress;
  phaseResponses: PhaseResponse[];
  selectedPhaseResponse: PhaseResponse;
}

export interface SelectOption {
  label: string;
  value: string;
}

export interface User {
  forename: string;
  surname: string;
  email: string;
  cultureName: string;
  isActive: boolean;
  id: string;
}

export interface EstimationsCurrentListQueryParams {
  projectTypeId?: string;
  estimationStage?: ApiEstimationStage;
  planningResponsibleId?: string;
  fulltextSearch?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: ApiSortEstimationsPerProjectBy;
  sortAscending?: boolean;
}

export interface UsersListQueryParams {
  projectTypeId?: string;
  estimationStage?: ApiEstimationStage;
  planningResponsibleId?: string;
  fulltextSearch?: string;
  pageNumber?: number;
  pageSize?: number;
  sortBy?: ApiSortUsersBy;
  sortAscending?: boolean;
}

export enum EstimationUnits {
  PD = 'PD',
  HOURS = 'HOURS',
  STORY_POINTS = 'STORY_POINTS',
  CURRENCY = 'CURRENCY',
}

export interface Metadata {
  id: string;
  name: string;
  description: string;
  order: number;
  isRequired: boolean;
  additionalFieldType: ApiEstimationAdditionalFieldType;
  selectableOptions: string[];
  guidedModeQuestion?: string | null;
  value?: string;
  isVisible?: boolean;
}

export type EstimationVersion = {
  index: string;
  estimationId: string;
  versionName: string;
  status: ApiEstimationStage;
  lastUpdatedAt: Dayjs;
  lastUpdatedBy: string;
  createdAt: Dayjs;
  createdBy: string;
  estimate: string;
};

export enum MetadataFieldMode {
  VIEW = 'view',
  EDIT = 'edit',
}

export type AvailableRoutes = ToSubOptions['to'];

export type OmitSafely<T, K extends keyof T> = Omit<T, K>;
